/**
 * Which product this hostname serves.
 *
 * ── ONE BUILD, TWO PRODUCTS, CHOSEN AT RUNTIME ──────────────────────────────────
 * The Business OS — ফাউন্ডার mode, ops, finance, HR, legal, corporate, projects and the
 * catalogue admin — is served on founder.oprappo.com and nowhere else. The customer's app
 * is served on oprappo.com, www and app. Both are the SAME Pages project and the SAME
 * build (founder decision 2026-09-18, Option A): a second project would mean a second
 * deploy, a second set of headers and a second place for the two to drift apart, to save
 * a few kilobytes the customer already never downloads (`business-os.ts` keeps the
 * Business OS off the first load).
 *
 * So the split is a function of `location.hostname`, and this is that function. It is
 * pure and takes the hostname as a parameter for the same reason `sw.ts § isCacheable`
 * takes the origin: a promise about which door serves what is only kept if a test can
 * call it with every hostname there is.
 *
 * ── THE THIRD ANSWER, AND WHY IT EXISTS ─────────────────────────────────────────
 * `both` mounts everything. It is what the harnesses see: the visual gate, the CSP
 * measurement and the responsive measurement all serve the build over
 * http://127.0.0.1, and each of them scores founder frames and customer frames in one
 * run. A preview deployment on *.pages.dev gets it too, because that is where a reviewer
 * opens a branch before it is live — and a preview that bounced every founder path to
 * production would review production, not the branch.
 *
 * `both` never redirects. That is also what keeps localhost from looping: a customer host
 * sends Business OS paths to founder.oprappo.com, the founder host serves them, and the
 * one host that could send a path to itself sends nothing anywhere.
 *
 * ── AN UNKNOWN HOSTNAME IS A CUSTOMER HOST ──────────────────────────────────────
 * Fail closed. A hostname nobody named here — a new alias, somebody's LAN address, a
 * proxy in front of the site — gets the customer's app, and the Business OS stays behind
 * the one door it was given. The opposite default would put the company's back office on
 * every hostname Cloudflare is ever asked to answer for.
 */

export type AppHost = 'founder' | 'customer' | 'both';

/** Where the Business OS lives. A customer host sends every Business OS path here. */
export const FOUNDER_ORIGIN = 'https://founder.oprappo.com';

/** Where the customer's app lives. The founder door's way out leads here. */
export const CUSTOMER_ORIGIN = 'https://oprappo.com';

const FOUNDER_HOSTS: ReadonlySet<string> = new Set(['founder.oprappo.com', 'founder.localhost']);

const CUSTOMER_HOSTS: ReadonlySet<string> = new Set([
  'oprappo.com',
  'www.oprappo.com',
  'app.oprappo.com',
]);

const LOCAL_HOSTS: ReadonlySet<string> = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

/**
 * `hostname` is `location.hostname`: no scheme, no port. Lower-cased and stripped of a
 * trailing dot first, because `OPRAPPO.COM.` is the same host to DNS and must be the same
 * host here — a mismatch on spelling would fall through to the customer default, which is
 * safe, but a founder typing the address in capitals should not be told the door is gone.
 */
export function appHost(hostname: string): AppHost {
  const host = hostname.trim().toLowerCase().replace(/\.$/, '');
  if (FOUNDER_HOSTS.has(host)) return 'founder';
  if (CUSTOMER_HOSTS.has(host)) return 'customer';
  if (LOCAL_HOSTS.has(host)) return 'both';
  // Pages preview deployments: <hash>.<project>.pages.dev and <branch>.<project>.pages.dev.
  // Matched as a suffix on a dot boundary, so `evilpages.dev` is not one.
  if (host.endsWith('.pages.dev')) return 'both';
  return 'customer';
}

/**
 * Where "back to the customer's app" goes from this hostname. On the founder host `/` is
 * the founder door itself (App.tsx sends it to /founder/enter), so a way out drawn as
 * `href="/"` would reload the screen it is leaving; it has to cross to the customer's
 * origin. Everywhere else the customer's app is this origin, and `/` stays `/` — so the
 * harnesses, which serve `both`, see the frame exactly as drawn.
 */
export function customerHome(hostname: string): string {
  return appHost(hostname) === 'founder' ? `${CUSTOMER_ORIGIN}/` : '/';
}
